/**
 * EditPeriodDialog Component
 *
 * Dialog for editing an existing OKR period's name and date range.
 * Also provides access to the Delete Period action.
 */

import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Typography,
    Divider,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { Edit, DeleteOutline } from '@mui/icons-material';
import { useOKRDashboardContext } from '../OKRDashboardContext';
import { okrApi } from '../../../services/okrApi';

export const EditPeriodDialog: React.FC = () => {
    const __muiTheme = useTheme();
    const isMobile = useMediaQuery(__muiTheme.breakpoints.down("sm"));
    const { isDarkMode, data, dialogs } = useOKRDashboardContext();
    const { setSuccess, setError, refetchData } = data;
    const {
        dialogs: dialogState,
        formState,
        updateFormField,
        closeEditPeriodDialog,
        openDeletePeriodDialog,
    } = dialogs;

    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        if (!formState.editPeriodId || !formState.editPeriodName.trim() ||
            !formState.editPeriodStartDate || !formState.editPeriodEndDate) return;

        if (new Date(formState.editPeriodStartDate) >= new Date(formState.editPeriodEndDate)) {
            setError('Start date must be before end date');
            return;
        }

        try {
            setSaving(true);
            await okrApi.updatePeriod(
                formState.editPeriodId,
                formState.editPeriodName.trim(),
                formState.editPeriodStartDate,
                formState.editPeriodEndDate
            );
            setSuccess('Period updated successfully');
            closeEditPeriodDialog();
            refetchData();
        } catch (err: any) {
            setError(err.message || 'Failed to update period');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteClick = () => {
        const periodData = {
            id: formState.editPeriodId,
            name: formState.editPeriodName,
        };
        closeEditPeriodDialog();
        openDeletePeriodDialog(periodData);
    };

    return (
        <Dialog
            open={dialogState.editPeriodDialog}
            onClose={closeEditPeriodDialog}
            fullScreen={isMobile}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: isDarkMode ? '#1e293b' : '#ffffff',
                    borderRadius: 3,
                },
            }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                        sx={{
                            width: 44,
                            height: 44,
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Edit sx={{ color: '#fff', fontSize: 24 }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Edit Period
                    </Typography>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    <TextField
                        autoFocus
                        label="Period Name"
                        fullWidth
                        value={formState.editPeriodName}
                        onChange={(e) => updateFormField('editPeriodName', e.target.value)}
                        placeholder="e.g. Q1 2026, Sprint Jan-Feb, H1 2026"
                        sx={{
                            '& .MuiOutlinedInput-root': { borderRadius: 2 },
                        }}
                    />
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            label="Start Date"
                            type="date"
                            fullWidth
                            value={formState.editPeriodStartDate}
                            onChange={(e) => updateFormField('editPeriodStartDate', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{
                                '& .MuiOutlinedInput-root': { borderRadius: 2 },
                            }}
                        />
                        <TextField
                            label="End Date"
                            type="date"
                            fullWidth
                            value={formState.editPeriodEndDate}
                            onChange={(e) => updateFormField('editPeriodEndDate', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{
                                '& .MuiOutlinedInput-root': { borderRadius: 2 },
                            }}
                        />
                    </Box>

                    <Divider sx={{ mt: 1 }} />

                    <Button
                        startIcon={<DeleteOutline />}
                        onClick={handleDeleteClick}
                        sx={{
                            color: '#ef4444',
                            fontWeight: 600,
                            justifyContent: 'flex-start',
                            textTransform: 'none',
                            '&:hover': {
                                bgcolor: 'rgba(239, 68, 68, 0.08)',
                            },
                        }}
                    >
                        Delete this period and all its data...
                    </Button>
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2.5, pt: 1 }}>
                <Button
                    onClick={closeEditPeriodDialog}
                    sx={{
                        color: isDarkMode ? '#94a3b8' : '#64748b',
                        fontWeight: 600,
                    }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={
                        !formState.editPeriodName.trim() ||
                        !formState.editPeriodStartDate ||
                        !formState.editPeriodEndDate ||
                        saving
                    }
                    sx={{
                        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                        fontWeight: 600,
                        borderRadius: 2,
                        px: 3,
                    }}
                >
                    {saving ? 'Saving...' : 'Save Changes'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditPeriodDialog;
